import { ChapterResponse, assetsChaptersBulkAddUpdateCreate } from '@goldcast/api/content';
import { isEqual } from 'radash';
import createStore from './store';
import {
  currentClip,
  isSplitsInTimelineEnabled,
  saveClipChanges,
  updateClipCaptionsLoadingState,
  updateClipMetadataWithId
} from './clip';
import { markCaptionsObsolete } from './captions';
import { sharedAPI } from './sharedAPI/sharedAPI';
import { addPendingUpdate, PendingEditType } from './pendingEditsStore';
import { Clip, ClipMetadata } from '@/player-config/src/types/clip-types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { showErrorToast } from '@/libs/toast/toast';

type ClipState = Pick<
  Clip['asset_metadata'],
  | 'start'
  | 'end'
  | 'edits'
  | 'deletes'
  | 'merges'
  | 'magicLayout'
  | 'title'
  | 'subtitle'
  | 'layout'
  | 'captions'
  | 'size'
  | 'intro'
  | 'introDuration'
  | 'outro'
  | 'outroDuration'
  | 'font_location'
  | 'highlight_type'
  | 'word_highlight_color'
  | 'is_edited'
  | 'template_id'
  | 'caption_positions'
  | 'caption_styles'
  | 'hide_borders'
  | 'text_highlight_color'
  | 'scenes'
  | 'selected_lang'
  | 'fit'
  | 'visible_video_ids'
  | 'speaker_label_styles'
  | 'player_config'
>;

type ClipHistoryState = {
  clipData?: Partial<ClipState>;
  chapters?: ChapterResponse[];
  entityUpdated?: string;
};

interface ClipHistory {
  index: number;
  states: ClipHistoryState[];
}

export const clipHistory = createStore<Record<string, ClipHistory>>({});

const cloneChapters = (chapters?: ChapterResponse[]) => chapters?.map(chapter => ({ ...chapter }));

const areChaptersEquivalent = (current?: ChapterResponse[], target?: ChapterResponse[]) => {
  if (current === target) {
    return true;
  }
  if (!current || !target) {
    return false;
  }
  if (current.length !== target.length) {
    return false;
  }
  const sortedCurrent = [...current].sort((a, b) => a.start_time - b.start_time);
  const sortedTarget = [...target].sort((a, b) => a.start_time - b.start_time);
  for (let index = 0; index < sortedCurrent.length; index++) {
    const left = sortedCurrent[index];
    const right = sortedTarget[index];
    if (left.id !== right.id || left.start_time !== right.start_time || left.title !== right.title) {
      return false;
    }
  }
  return true;
};

const getCurrentChaptersForClip = (clipId: string): ChapterResponse[] | undefined => {
  return sharedAPI.getSnapshot()[clipId]?.transcriptStore?.chapterData;
};

const hasClipStateChanged = (currentClipMetadata: Clip['asset_metadata'], targetClipState?: Partial<ClipState>) => {
  if (!targetClipState) {
    return false;
  }
  const targetClipStateKeys = Object.keys(targetClipState) as (keyof ClipState)[];
  if (targetClipStateKeys.length === 0) {
    return false;
  }
  return targetClipStateKeys.some(key => !isEqual(currentClipMetadata[key], targetClipState[key]));
};

const createClipHistoryState = (clipMetadata: ClipMetadata): ClipState => ({
  start: clipMetadata.start,
  end: clipMetadata.end,
  edits: clipMetadata.edits,
  deletes: clipMetadata.deletes,
  merges: clipMetadata.merges,
  magicLayout: clipMetadata.magicLayout,
  title: clipMetadata.title,
  subtitle: clipMetadata.subtitle,
  captions: clipMetadata.captions,
  size: clipMetadata.size,
  layout: clipMetadata.layout,
  intro: clipMetadata.intro,
  introDuration: clipMetadata.introDuration,
  outro: clipMetadata.outro,
  outroDuration: clipMetadata.outroDuration,
  font_location: clipMetadata.font_location,
  highlight_type: clipMetadata.highlight_type,
  word_highlight_color: clipMetadata.word_highlight_color,
  is_edited: clipMetadata.is_edited,
  template_id: clipMetadata.template_id,
  caption_positions: clipMetadata.caption_positions,
  caption_styles: clipMetadata.caption_styles,
  hide_borders: clipMetadata.hide_borders,
  text_highlight_color: clipMetadata.text_highlight_color,
  scenes: clipMetadata.scenes,
  selected_lang: clipMetadata.selected_lang,
  fit: clipMetadata.fit,
  visible_video_ids: clipMetadata.visible_video_ids,
  speaker_label_styles: clipMetadata.speaker_label_styles,
  player_config: clipMetadata.player_config
});

const replayChaptersForUndoRedo = ({
  clipId,
  broadcastId,
  chapters
}: {
  clipId: string;
  broadcastId?: string;
  chapters: ChapterResponse[];
}) => {
  EventBus.dispatch(CustomEvents.UpdateChapters, {
    chapterData: chapters,
    chaptersLastUpdatedAt: new Date().toISOString()
  });

  if (!broadcastId) {
    return;
  }

  const chapterUpdatePromise = assetsChaptersBulkAddUpdateCreate({
    body: chapters,
    queryParams: {
      content_id: broadcastId
    }
  });

  addPendingUpdate({
    clipId,
    updatePromise: chapterUpdatePromise,
    version: Date.now(),
    editType: PendingEditType.chapter
  });

  void chapterUpdatePromise.catch(error => {
    window.logger?.error('Failed to sync chapters for undo/redo', error);
    showErrorToast('Failed to sync chapters. Please try again.');
  });
};

const appendHistoryState = (clipId: string, state: ClipHistoryState) => {
  clipHistory.update((data: Record<string, ClipHistory>) => {
    const history = data[clipId];
    if (!history) {
      return {
        ...data,
        [clipId]: {
          index: 0,
          states: [state]
        }
      };
    }

    return {
      ...data,
      [clipId]: {
        ...history,
        states: [...history.states.slice(0, history.index + 1), state],
        index: history.index + 1
      }
    };
  });
};

const injectHistoryStateWithChapters = ({
  clipId,
  chapters,
  indexOffset,
  onlyIfMissing = false
}: {
  clipId: string;
  chapters: ChapterResponse[] | undefined;
  indexOffset: number;
  onlyIfMissing?: boolean;
}) => {
  clipHistory.update(data => {
    const history = data[clipId];
    if (!history) {
      return data;
    }

    const targetIndex = history.index + indexOffset;
    if (targetIndex < 0 || targetIndex >= history.states.length) {
      return data;
    }

    if (onlyIfMissing && history.states[targetIndex].chapters !== undefined) {
      return data;
    }

    const updatedStates = [...history.states];
    updatedStates[targetIndex] = {
      ...updatedStates[targetIndex],
      chapters: cloneChapters(chapters)
    };

    return {
      ...data,
      [clipId]: {
        ...history,
        states: updatedStates
      }
    };
  });
};

export function initHistory(clipId: string, clipMetadata: ClipMetadata) {
  const chapters = getCurrentChaptersForClip(clipId);
  clipHistory.update(data => ({
    ...data,
    [clipId]: {
      index: 0,
      states: [
        {
          clipData: createClipHistoryState(clipMetadata),
          chapters: cloneChapters(chapters)
        }
      ]
    } as ClipHistory
  }));
}

export function updateHistory(
  clipId: string,
  clipMetadata: ClipMetadata,
  chapters?: ChapterResponse[],
  entityUpdated?: string
) {
  appendHistoryState(clipId, {
    clipData: createClipHistoryState(clipMetadata),
    ...(chapters !== undefined ? { chapters: cloneChapters(chapters) } : {}),
    ...(entityUpdated ? { entityUpdated } : {})
  });
}

export function appendChapterHistory(clipId: string, chapters: ChapterResponse[] | undefined) {
  appendHistoryState(clipId, { chapters: cloneChapters(chapters) });
}

export function injectCurrentHistoryWithChapters(clipId: string, chapters: ChapterResponse[] | undefined) {
  injectHistoryStateWithChapters({ clipId, chapters, indexOffset: 0 });
}

export function injectPreviousHistoryWithChaptersIfMissing(clipId: string, chapters: ChapterResponse[] | undefined) {
  injectHistoryStateWithChapters({ clipId, chapters, indexOffset: -1, onlyIfMissing: true });
}

export function undoChange(clipId: string) {
  const history = clipHistory.getSnapshot()[clipId];
  if (!history || history.index <= 0) {
    return;
  }
  const undoneAction = history.states[history.index]?.entityUpdated;
  window.analytics?.track('ContentLabEvent', {
    name: 'Undo',
    clipId,
    undoneAction
  });
  const newHistoryIndex = history.index - 1;
  applyUndoRedo(clipId, history, newHistoryIndex, true);
}

export function redoChange(clipId: string) {
  const history = clipHistory.getSnapshot()[clipId];
  if (!history || history.index >= history.states.length - 1) {
    return;
  }
  const newHistoryIndex = history.index + 1;
  applyUndoRedo(clipId, history, newHistoryIndex);
}

function applyUndoRedo(clipId: string, _clipHistory: ClipHistory, newHistoryIndex: number, isUndo: boolean = false) {
  const currentClipValue = currentClip.getSnapshot()[clipId];
  const targetState = _clipHistory.states[newHistoryIndex];
  if (!currentClipValue || !targetState) {
    return;
  }

  const currentAssetMetadata = currentClipValue.asset_metadata;
  const { chapters: targetChapters, clipData: targetClipData } = targetState;
  const shouldUpdateClipState = hasClipStateChanged(currentAssetMetadata, targetClipData);

  if (shouldUpdateClipState) {
    markCaptionsObsolete({ clipId });
    updateClipCaptionsLoadingState({ clipId, loading: true });

    const finalUpdateValue = { ...currentAssetMetadata, ...targetClipData };
    const captionsPositions = finalUpdateValue.caption_positions;
    if (captionsPositions) {
      const { x, y, width, height } = captionsPositions;

      EventBus.dispatch(CustomEvents.UpdateCaptionsPosition, {
        left: x,
        top: y,
        width,
        height
      });
    }

    updateClipMetadataWithId(clipId, finalUpdateValue, false);

    const hadSplits = isSplitsInTimelineEnabled(currentClipValue);
    const newHasSplits = isSplitsInTimelineEnabled(currentClip.getSnapshot()[clipId]);

    if (hadSplits !== newHasSplits) {
      window.analytics?.track('ContentLabEvent', {
        name: 'UndoRedoSplitsChanged',
        isSuccess: false,
        clipId,
        addedSplits: newHasSplits,
        removedSplits: hadSplits,
        action: isUndo ? 'Undo' : 'Redo'
      });
    }
  }

  const currentChapters = getCurrentChaptersForClip(clipId);
  if (targetChapters !== undefined && !areChaptersEquivalent(currentChapters, targetChapters)) {
    replayChaptersForUndoRedo({
      clipId,
      broadcastId: currentClipValue.broadcast,
      chapters: targetChapters
    });
  }

  clipHistory.update(data => ({ ...data, [clipId]: { ...data[clipId], index: newHistoryIndex } }));

  if (shouldUpdateClipState) {
    saveClipChanges({
      clipId,
      shouldGenerateThumbnail: false,
      shouldAddToHistory: false,
      isEditedState: targetClipData?.is_edited ?? currentAssetMetadata.is_edited
    });
  }
}
